import { useEffect, useMemo } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { PAGE_SIZE_DEFAULT } from '@bower/shared/types/pagination';
import type { PaginatedResponse } from '@bower/shared';
import { logger } from '../../services/logging.service';

/** Safety cap on auto-fetch-all mode: stop after this many pages even if the
 * server says `hasMore`. At `PAGE_SIZE_MAX` (100) this is 1000 rows — enough
 * for a bulk-ops use case, bounded enough to not runaway if a
 * backend filter breaks. */
const EAGER_FETCH_PAGE_CAP = 10;

/**
 * Shared wrapper around TanStack `useInfiniteQuery` for any endpoint that
 * returns the canonical `PaginatedResponse<T>` shape.
 *
 * Why this exists:
 * - Every list page (Collections, Artifacts, Trash) needs the same wiring:
 *   carry `nextCursor` through `getNextPageParam`, send it back as a query
 *   param, stop on `hasMore === false`.
 * - Doing it once here means a single place to fix bugs, tune `staleTime`,
 *   or change the wire format.
 *
 * Contract:
 * - `fetchPage` is called with `{ cursor, limit }`. On the first page
 *   `cursor` is `undefined`; on subsequent pages it's whatever the previous
 *   page returned as `nextCursor` (an opaque string — do NOT parse).
 * - The hook returns the standard TanStack result AND derived helpers:
 *   - `flatItems` — every page's items concatenated, for components that
 *     expect a flat array
 *   - `totalCount` — the server only sends this on page 1; we cache it
 *     from the first page that had it so components can read it even while
 *     the user is deep into pagination
 *   - `isEndOfList` — `hasMore === false` on the latest page (handy for
 *     "that's everything" footer UI)
 */
export interface UsePaginatedOptions<T> {
  /** Stable queryKey — include any filter/workspace values that invalidate the cache. */
  queryKey: readonly unknown[];

  /** Fetch one page. Called with opaque cursor + caller's limit. */
  fetchPage: (args: { cursor: string | undefined; limit: number }) => Promise<PaginatedResponse<T>>;

  /** Server-enforced cap applies regardless, but pass through for UX (e.g. mobile list = 25). */
  limit?: number;

  /** Gate the query (same semantics as TanStack `enabled`). */
  enabled?: boolean;

  /** Override default staleTime; leave undefined to inherit queryClient defaults. */
  staleTime?: number;

  /**
   * When true, the hook auto-fires `fetchNextPage` whenever a page returns
   * with `hasMore: true`, up to `EAGER_FETCH_PAGE_CAP`. Intended for pages
   * whose UI still uses client-side filtering / bulk-ops where
   * the consumer needs the full dataset in memory. Leave `false` (default)
   * for true infinite-scroll UIs that load on user intent.
   */
  eagerlyFetchAll?: boolean;
}

export function useInfinitePaginated<T>({
  queryKey,
  fetchPage,
  limit = PAGE_SIZE_DEFAULT,
  enabled = true,
  staleTime,
  eagerlyFetchAll = false,
}: UsePaginatedOptions<T>) {
  type Page = PaginatedResponse<T>;

  const options: UseInfiniteQueryOptions<Page, Error, { pages: Page[]; pageParams: unknown[] }> = {
    queryKey,
    queryFn: ({ pageParam }) => fetchPage({ cursor: pageParam as string | undefined, limit }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: Page) => lastPage.nextCursor ?? undefined,
    enabled,
    staleTime,
    // Keep the previous query's rows onscreen while a new query (triggered
    // by a search/sort/filter change → queryKey change) is in flight.
    // Without this, every keystroke past the debounce would flash the list
    // empty. Consumers can inspect `isPlaceholderData` on the returned
    // query to dim the rows while they wait for the fresh page.
    placeholderData: keepPreviousData,
  };

  const query = useInfiniteQuery(options);

  // Derived views. `useMemo` keeps referential stability so downstream
  // `React.memo` children don't re-render on every parent render when the
  // underlying pages haven't changed.
  const flatItems = useMemo<T[]>(() => {
    if (!query.data) return [];
    return query.data.pages.flatMap((p) => p.items as T[]);
  }, [query.data]);

  const totalCount = useMemo<number | null>(() => {
    if (!query.data) return null;
    // Server sends totalCount only on page 1 (when cursor was absent).
    // Prefer the FIRST non-null value we've seen — that's the definitive
    // count from the first unfiltered fetch. Filters rotate the queryKey,
    // so a filter change produces a brand-new query whose page 1 carries
    // the fresh count; we don't need to worry about stale counts surviving
    // filter changes.
    for (const page of query.data.pages) {
      if (page.totalCount != null) return page.totalCount;
    }
    return null;
  }, [query.data]);

  const isEndOfList = useMemo<boolean>(() => {
    if (!query.data) return false;
    const last = query.data.pages[query.data.pages.length - 1];
    return last != null && last.hasMore === false;
  }, [query.data]);

  // Eager-fetch-all mode: chain `fetchNextPage` whenever the previous page
  // has more AND we haven't hit the safety cap. The condition is
  // re-evaluated each render, so TanStack's own `isFetchingNextPage`
  // guard (inside `fetchNextPage`) prevents overlapping calls. We stop
  // on `EAGER_FETCH_PAGE_CAP` to bound memory regardless of server state.
  //
  // We warn once when the cap is hit so a regressed server-side filter
  // that always sets `hasMore: true` surfaces in the console rather than
  // silently truncating the UI at 1000 rows.
  useEffect(() => {
    if (!eagerlyFetchAll) return;
    if (!query.data || query.isFetchingNextPage) return;
    if (!query.hasNextPage) return;
    if (query.data.pages.length >= EAGER_FETCH_PAGE_CAP) {
      // Route through the structured logger so this lands in GCP Logs
      // alongside every other frontend warning. If a regressed server
      // filter ever pins `hasMore: true`, oncall can filter on this
      // message instead of waiting for a user bug report about a list
      // capped at 1000 rows.
      logger.warn('useInfinitePaginated: eagerlyFetchAll safety cap hit', {
        component: 'useInfinitePaginated',
        metadata: {
          cap: EAGER_FETCH_PAGE_CAP,
          pagesFetched: query.data.pages.length,
          // queryKey is a readonly tuple of primitives/plain objects;
          // stringify so GCP Logs treats it as a searchable string.
          queryKey: JSON.stringify(queryKey),
        },
      });
      return;
    }
    void query.fetchNextPage();
  }, [
    eagerlyFetchAll,
    query.data,
    query.hasNextPage,
    query.isFetchingNextPage,
    query.fetchNextPage,
    queryKey,
  ]);

  return {
    ...query,
    flatItems,
    totalCount,
    isEndOfList,
  };
}
